import { Box, Card, Divider, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import type { DailyDashboardIssue } from '../../../backendsdk/api';
import CoachingSessionComponent from './CoachingSessionComponent';
import { FleetDriversView, SyntheticCoachSession } from './Defs';

/** API session object (COACHING issue_details.sessions[] item). */
export type CoachingSessionDetail = SyntheticCoachSession & { image?: string };

function toSyntheticSession(s: CoachingSessionDetail): SyntheticCoachSession {
    return {
        session_id: s.session_id,
        driver_name: s.driver_name,
        license_plate: s.license_plate,
        is_saved: !!s.is_saved,
        sent: s.sent,
        done: s.done,
        events: s.events ?? [],
    };
}

interface CoachIssueCardProps {
    item: DailyDashboardIssue;
    cardId: number;
    viewMode: FleetDriversView;
}

const CoachIssueCard: React.FC<CoachIssueCardProps> = (props) => {
    const isOdd = props.cardId % 2 == 1;
    const details = props.item.issue_details ?? {};
    const sessionsFromApi = useMemo(
        () => (Array.isArray(details.sessions) ? (details.sessions as CoachingSessionDetail[]) : []),
        [details.sessions],
    );

    const issueList = Array.isArray(details.issue) ? details.issue : [];
    const remediationList = Array.isArray(details.remediation) ? details.remediation : [];

    const coachSessionComponents = useMemo(() => {
        return sessionsFromApi.map((s, index) => {
            const sessionImage = typeof s.image === 'string' ? `data:image/png;base64,${s.image}` : undefined;
            return (
                <React.Fragment key={s.session_id}>
                    {index > 0 && <Divider />}
                    <CoachingSessionComponent session={toSyntheticSession(s)} sessionImage={sessionImage} />
                </React.Fragment>
            );
        });
    }, [sessionsFromApi]);

    return (
        <Card
            sx={{
                my: 1,
                p: 2,
                width: 'clamp(40%, 700px, 80%)',
                backgroundColor: isOdd ? '#e0e0e0' : '#cdcdcd',
                borderRadius: '5px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box>
                    <Typography variant="caption" sx={{ color: '#606060', fontSize: '12px' }}>
                        Issue #{props.cardId}
                    </Typography>
                    {issueList.map((desc: string, idx: number) => (
                        <Typography
                            variant="subtitle1"
                            key={idx}
                            sx={{ fontSize: '18px' }}
                            component="div"
                            dangerouslySetInnerHTML={{ __html: desc }}
                        />
                    ))}
                </Box>
                <Box>{coachSessionComponents}</Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#606060', fontSize: '12px' }}>
                        Remediation
                    </Typography>
                    {remediationList.map((desc: string, idx: number) => (
                        <Typography
                            variant="subtitle1"
                            key={idx}
                            sx={{ fontSize: '18px' }}
                            component="div"
                            dangerouslySetInnerHTML={{ __html: desc }}
                        />
                    ))}
                </Box>
            </Box>
        </Card>
    );
};

export default CoachIssueCard;
